<template>
  <div v-bind:class="isInNewMode ? '':'task-details'">
    <section class="section">
      <b-loading :active="isLoading"></b-loading>
      <div class="container">
        <div class="columns">
          <div class="column column is-full">
            <div class="box">
              <div class="columns" style="margin-top: 16px">
                <div class="column">
                  <header class="heading">
                    <p class="modal-card-title">{{ pageTitle }}</p>
                  </header>
                </div>
                <div v-if="!isEditMode" class="column is-narrow">
                  <b-button class="button" @click="backClicked" icon-left="arrow-left-bold">
                    {{ $t('common.back') }}
                  </b-button>
                </div>
                <div v-if="!isEditMode && ((!this.is_template && currentUserAccess('TaskDetails', this.UserActions.UPDATE, this.project_id)) 
                                          || (this.is_template && currentUserAccess('ProjectTemplates', this.UserActions.UPDATE)))"
                     class="column is-narrow">
                  <b-button class="button is-primary" @click="editClicked" icon-left="pencil">
                    {{ $t('common.edit') }}
                  </b-button>
                </div>
                <div v-if="!isEditMode && ((!this.is_template && currentUserAccess('TaskDetails', this.UserActions.DELETE, this.project_id)) 
                                          || (this.is_template && currentUserAccess('ProjectTemplates', this.UserActions.DELETE)))"
                     class="column is-narrow">
                  <b-button class="button is-primary" type="is-danger" @click="deleteClicked" icon-left="delete">
                    {{ $t('common.delete') }}
                  </b-button>
                </div>
              </div>
              <div class="columns" style="margin-top: 16px">
                <div class="column is-half">
                  <b-field v-if="isEditMode" :label="$t('task-details-view.task-title-field')">
                    <BInputWithValidation rules="required" v-model="taskData.title"
                                          :placeholder="$t('task-details-view.task-title-placeholder')"
                                          tabindex="1"></BInputWithValidation>
                  </b-field>
                  <b-field :label="$t('task-details-view.task-category-field')">
                    <b-taginput
                        ref="categoryInput"
                        v-if="isEditMode"
                        maxtags="1"
                        v-model="selectedCategories"
                        :data="filteredCategories"
                        :loading="isFetchingCategories"
                        :has-counter="false"
                        :open-on-focus="true"
                        :allow-new="false"
                        autocomplete
                        :remove-on-keys=[]
                        :confirm-keys=[]
                        :placeholder="$t('task-details-view.task-category-placeholder')"
                        tabindex="2"
                        @typing="getFilteredCategories"
                        @input="onCategoriesInput"
                        @blur="onCategoryInputBlur">
                    </b-taginput>
                    <b-taglist v-else>
                      <b-tag type="is-info is-light" size="is-medium">
                        {{ selectedCategories[0] }}
                      </b-tag>
                    </b-taglist>
                  </b-field>
                  <b-field :label="$t('task-details-view.task-description-field')">
                    <b-input v-if="isEditMode" v-model="taskData.description"
                             :placeholder="$t('task-details-view.task-description-placeholder')" tabindex="4"
                             type="textarea"></b-input>
                    <label v-else style="white-space: pre-wrap;">{{ taskData.description }}</label>
                  </b-field>
                </div>
                <div class="column">
                  <b-field :label="$t('task-details-view.task-quantity-placeholder')" class="no-margin">
                    <div v-if="isEditMode" class="columns">
                      <b-input v-if="isEditMode" v-model="taskData.meta.estimation.quantity" type="number" step="any"
                               min="0" class="column estimation-value"
                               :placeholder="$t('task-details-view.task-quantity-placeholder')"
                               tabindex="5">
                      </b-input>
                      <b-select v-if="isEditMode" v-model="taskData.unit"
                                tabindex="6" class="column"
                                :placeholder="$t('task-details-view.task-quantityunit-placeholder')">
                        <option
                            v-for="option in quantityUnits"
                            :value="option.id"
                            :key="option.id">
                          {{ $t(option.translationKey) }}
                        </option>
                      </b-select>
                    </div>
                    <label v-else>{{ getFormattedQuantity(taskData.meta.estimation, taskData.unit) }}
                    </label>
                  </b-field>
                  <div class="columns no-margin">
                    <div class="column is-half">
                      <b-field :label="$t('task-details-view.task-internal-personnel-number')">
                        <b-input v-if="isEditMode" v-model="taskData.meta.estimation.number_of_internal_personnel"
                                 type="number" min=0
                                 step="1"
                                 :placeholder="$t('task-details-view.task-internal-personnel-number-placeholder')"
                                 tabindex="7"></b-input>
                        <label v-else>{{ taskData.meta.estimation.number_of_internal_personnel }}</label>
                      </b-field>
                    </div>
                    <div class="column is-half">
                      <b-field :label="$t('task-details-view.task-external-personnel-number')">
                        <b-input v-if="isEditMode" v-model="taskData.meta.estimation.number_of_external_personnel"
                                 type="number" min=0
                                 step="1"
                                 :placeholder="$t('task-details-view.task-external-personnel-number-placeholder')"
                                 tabindex="8"></b-input>
                        <label v-else>{{ taskData.meta.estimation.number_of_external_personnel }}</label>
                      </b-field>
                    </div>
                  </div>
                  <b-field :label="$t('task-details-view.task-performance')">
                    <b-input v-if="isEditMode" v-model="taskData.meta.estimation.performance" type="number" min=0
                             step="any"
                             :placeholder="$t('task-details-view.task-performance-placeholder')"
                             tabindex="9"></b-input>
                    <label v-else>{{ getFormattedPerformance(taskData.meta.estimation.performance, taskData.unit)}}</label>
                  </b-field>
                  <label v-if="isEditMode" class="is-size-7">{{$t('task-details-view.task-performance-note')}}</label>
                </div>
              </div>
              <div class="columns is-mobile" style="margin-top: 16px" v-if="isEditMode">
                <div class="column is-narrow">
                  <b-button class="button" @click="cancelClicked" icon-left="close-thick" tabindex="20">
                    {{ $t('common.cancel') }}
                  </b-button>
                </div>
                <div class="column is-narrow">
                  <b-button v-if="(!this.is_template && currentUserAccess('TaskDetails', this.UserActions.UPDATE, this.project_id)) 
                                  || (this.is_template && currentUserAccess('ProjectTemplates', this.UserActions.UPDATE))"
                            class="button is-primary" @click="saveClicked" icon-left="content-save"
                            tabindex="21">
                    {{ $t('common.save') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import firebase from "firebase/app";
import BInputWithValidation from "./inputs/BInputWithValidation";

export default {
  name: "TaskDetails",
  components: {
    BInputWithValidation
  },
  props: {
    'project_id': {
      type: String,
    },
    'task_id': {
      type: String,
    },
    'is_template': {
      type: Boolean,
    }
  },
  data() {
    return {
      taskData: {
        meta: {
          estimation: {}
        },
        unit: ""
      },
      isLoading: false,
      isEditMode: false,
      isFetchingCategories: false,
      categoriesData: [],
      filteredCategories: [],
      selectedCategories: []
    }
  },
  methods: {
    readTaskData: function () {
      this.isLoading = true;
      const functions = firebase.app().functions("europe-west3");
      const getTaskFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getTaskFunction({
        action: "get_task_by_id",
        project_id: this.project_id,
        document_id: this.task_id,
        fill_meta: true
      }).then((result) => {
        console.log("read task result: ", result.data);
        this.taskData = result.data["tasks"];
        if (!this.taskData.meta.estimation)
          this.taskData.meta.estimation = {};
        const category = this.taskData.category
        if (category !== undefined && category !== "") {
          this.selectedCategories[0] = category
        }
        this.isLoading = false;
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting tasks: ", error);
      });
    },
    createTask: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions("europe-west3");
      const insertTaskFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "insert_task";
      console.log("create task payload:", updateData)
      insertTaskFunction(updateData).then((result) => {
        this.isLoading = false
        console.log("create task result: ", result.data);
        this.showSuccess(this.$t('messages.task-created'));
        this.$emit('taskAdded');
      }).catch((error) => {
        this.isLoading = false
        this.showError(error.message)
      });
    },
    updateTask: function (updateData) {
      this.isLoading = true;
      const functions = firebase.app().functions("europe-west3");
      const updateTaskFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "update_task";
      console.log("create task payload:", updateData)
      updateTaskFunction(updateData).then((result) => {
        this.isLoading = false;
        this.isEditMode = false;
        console.log("update result: ", result.data);
        this.showSuccess(this.$t('messages.task-updated'));
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error updating task: ", error);
      });
    },
    deleteTask: function () {
      this.isLoading = true;
      const functions = firebase.app().functions("europe-west3");
      const deleteTaskFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deleteTaskFunction({
        action: "delete_task",
        project_id: this.project_id,
        document_id: this.task_id,
      }).then((result) => {
        this.isLoading = false;
        this.isEditMode = false;
        console.log("delete task result: ", result.data);
        this.showSuccess(this.$t('messages.task-deleted'));
        this.$emit('taskDeleted');
        this.$router.back();
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting delete result: ", error);
      });
    },
    readCategoriesData: function () {
      this.isFetchingCategories = true
      const functions = firebase.app().functions("europe-west3");
      const getCategoriesFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getCategoriesFunction({
        action: "get_categories",
        collections: ["tasks"]
      }).then((result) => {
        this.isFetchingCategories = false
        console.log("read categories result: ", result.data);
        this.categoriesData = result.data["tasks"].category
        this.filteredCategories = this.categoriesData
      }).catch((error) => {
        this.isFetchingCategories = false
        this.showError(error.message)
        console.log("Error getting categories: ", error);
      });
    },
    /*
     UI Events
     */
    editClicked: function () {
      this.isEditMode = !this.isEditMode;
    },
    deleteClicked: function () {
      this.$buefy.dialog.confirm({
        title: this.$t('task-details-view.delete-task-confirm-title').toString(),
        message: this.$t('task-details-view.delete-task-confirm-message').toString(),
        confirmText: this.$t('task-details-view.delete-task-confirm-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        hasIcon: true,
        icon: 'delete',
        onConfirm: () => {
          this.deleteTask();
        }
      });
    },
    cancelClicked: function () {
      if (this.isInNewMode) {
        this.$emit('close');
      } else if (this.isEditMode) {
        this.readTaskData();
        this.isEditMode = false;
      }
    },
    backClicked: function () {
      this.$router.back();
    },
    saveClicked: function () {
      const dataFieldsToUpdate = {
        project_id: this.project_id,
        title: this.taskData.title,
        description: this.taskData.description,
      }
      let estimation = this.taskData.meta.estimation;
      let hasEstimation = true;

      // Estimation verification
      if (Object.keys(estimation).length !== 0) {
        if (estimation.quantity !== undefined && estimation.quantity !== ""
            && estimation.quantity != null && estimation.quantity > 0) {
          estimation.quantity = parseFloat(estimation.quantity);
          if (this.taskData.unit === undefined || this.taskData.unit === "" || this.taskData.unit == null) {
            this.taskData.unit = this.defaultValues.quantityUnitId;
          }
        } else {
          hasEstimation = false;
        }

        if (estimation.number_of_internal_personnel !== undefined && estimation.number_of_internal_personnel !== ""
            && estimation.number_of_internal_personnel != null && estimation.number_of_internal_personnel > 0) {
          estimation.number_of_internal_personnel = parseInt(estimation.number_of_internal_personnel);
        } else {
          estimation.number_of_internal_personnel = 0;
        }
        if (estimation.number_of_external_personnel !== undefined && estimation.number_of_external_personnel !== ""
            && estimation.number_of_external_personnel != null && estimation.number_of_external_personnel > 0) {
          estimation.number_of_external_personnel = parseInt(estimation.number_of_external_personnel);
        } else {
          estimation.number_of_external_personnel = 0;
        }
        if (estimation.number_of_internal_personnel + estimation.number_of_external_personnel === 0) {
          hasEstimation = false;
        }
        if (estimation.performance !== undefined && estimation.performance !== ""
            && estimation.performance != null && estimation.performance > 0) {
          estimation.performance = parseFloat(estimation.performance);
        } else {
          hasEstimation = false;
        }
      }
      if (this.selectedCategories[0] !== undefined) {
        dataFieldsToUpdate.category = this.selectedCategories[0]
      }
      if (hasEstimation === true) {
        dataFieldsToUpdate.estimation = estimation;
        dataFieldsToUpdate.unit = this.taskData.unit
        if (this.isInNewMode) {
          this.createTask(dataFieldsToUpdate);
        } else {
          dataFieldsToUpdate.document_id = this.task_id;
          this.updateTask(dataFieldsToUpdate);
        }
      } else {
        this.$buefy.dialog.confirm({
          title: this.$t('task-details-view.modify-estimation-confirm-title').toString(),
          message: this.$t('task-details-view.modify-estimation-confirm-message').toString(),
          confirmText: this.$t('task-details-view.modify-estimation-confirm-button').toString(),
          cancelText: this.$t('common.cancel').toString(),
          type: 'is-warning',
          hasIcon: true,
          icon: 'alert-circle',
          onConfirm: () => {
            if (this.isInNewMode) {
              this.createTask(dataFieldsToUpdate);
            } else {
              dataFieldsToUpdate.document_id = this.task_id;
              this.updateTask(dataFieldsToUpdate);
              this.readTaskData();
            }
          }
        });
      }
    },
    onCategoriesInput() {
      this.filteredCategories = this.categoriesData
    },
    getFilteredCategories(text) {
      this.filteredCategories = this.categoriesData.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onCategoryInputBlur() {
      this.$refs.categoryInput.newTag = '';
    }
  },
  mounted() {
    if ((!this.is_template && !this.currentUserAccess("TaskDetails", this.UserActions.VIEW, this.project_id))
        || (this.is_template && !this.currentUserAccess("ProjectTemplates", this.UserActions.VIEW))) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => {
      });
    }
    this.readCategoriesData()
    if (this.isInNewMode) {
      console.log("creating new task");
      this.taskData.meta = {};
      this.taskData.meta.estimation = {};
      this.isEditMode = true;
    } else {
      console.log("edit task: ", this.task_id);
      this.isEditMode = false;
      this.readTaskData();
    }
  },
  computed: {
    isInNewMode() {
      return this.task_id === "new";
    },
    pageTitle() {
      if (this.isInNewMode) {
        return this.$t('task-details-view.new-task-title')
      } else if (this.isEditMode) {
        return this.$t('task-details-view.edit-task-title') + " " + this.getAdjustedTitle(this.taskData.title)
      } else {
        return this.$t('task-details-view.view-task-title') + " " + this.getAdjustedTitle(this.taskData.title)
      }
    },
    isTemplateMode() {
      return this.is_template
    }
  }
};
</script>

<style scoped>
.estimation-value {
  max-width: 50%;
}

.no-margin {
  margin-bottom: 0px !important;
}
</style>